import { useFormatter, useTranslations } from 'next-intl';
import { useMemo } from 'react';

/**
 * 格式化时间显示
 */
const formatRelativeTime = (
  diffMs: number,
  publishTime: string,
  format: ReturnType<typeof useFormatter>,
  t: ReturnType<typeof useTranslations<'notifications.time'>>,
): string => {
  if (!publishTime) return '';
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  // 0-59s内，展示"刚刚"
  if (diffSeconds < 60) {
    return t('justNow');
  }
  // 1min-59min59s内，展示"x分钟前"，四舍五入
  if (diffMinutes < 60) {
    return t('minute', { x: diffMinutes });
  }
  // 1h-23h59min59s内，展示"x小时前"，四舍五入
  if (diffHours < 24) {
    return t('hour', { x: diffHours });
  }
  // 剩余展示具体年-月-日
  return format.dateTime(new Date(publishTime), {
    day: '2-digit',
    month: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    year: 'numeric',
  });
};

/**
 * 通知时间 Hook
 *
 * 用于显示通知的相对时间，根据时间差智能选择更新间隔
 *
 * @param publishTime 发布时间字符串
 * @returns 格式化后的相对时间字符串
 *
 * @example
 * ```tsx
 * const relativeTimeText = useNotificationTime('2024-01-01T10:00:00Z');
 * return <div>{relativeTimeText}</div>; // 显示 "2小时前" 或 "01/01/2024"
 * ```
 */
export const useNotificationTime = (publishTime: string): string => {
  const t = useTranslations('notifications.time');
  const format = useFormatter();
  const timeDiff = useMemo(() => {
    return Date.now() - new Date(publishTime).getTime();
  }, [publishTime]);

  return formatRelativeTime(timeDiff, publishTime, format, t);
};
